import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { FormCheckbox, FormField, FormRadioGroup } from '../../common/form';
import { FormInputNumber } from '../../common/form';
import { FormTimePicker } from '../../common/form/input/FormTimePicker';
import { useSystemClassifierSelectOptions } from '../../configuration';
import translations from '../translations/LoadingPeriodFields.translations.json';
import { ExecutionMethod } from './types';
import { usePeriodLoadingType } from './utils';
import { DataObjectGroupType } from '../types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'loading-period-fields'
);
addTranslations(translations);

export function LoadingPeriodFields({
  executionMethod,
  isInput,
  groupType,
}: {
  groupType: DataObjectGroupType;
  isInput: boolean;
  executionMethod?: string;
}) {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const options = usePeriodLoadingOptions(executionMethod);
  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION']);
  const { periodLoadingType } = usePeriodLoadingType();
  const fixed = periodLoadingType === 'FIXED';

  const changeLoadingType = useCallback(
    (e: RadioChangeEvent) => {
      const loadingTypeId = e.target.value as number;
      const loadingType = options.find(
        (option) => option.value === loadingTypeId
      )?.code;
      const periodCount = getValues('execution.executionPeriods.length');
      let value: number | null;
      if (loadingType !== 'VARIABLE') {
        // reset all period counts
        value = null;
      } else {
        // set default period count if it exists
        value = getValues('execution.periodCount');
      }
      for (let i = 0; i < periodCount; i++) {
        setValue(`execution.executionPeriods.${i}.periodCount`, value, {
          shouldDirty: true,
        });
      }
    },
    [getValues, options, setValue]
  );

  return (
    <>
      {groupType === DataObjectGroupType.O && (
        <FormField
          name="execution.sourceDataVersionId"
          label={t('sourceDataVersionId.label')}
          required
        >
          <FormRadioGroup
            name="execution.sourceDataVersionId"
            options={sourceDataVersionOptions}
          />
        </FormField>
      )}
      {(executionMethod === ExecutionMethod.PERIODIC ||
        executionMethod === ExecutionMethod.CALENDAR) && (
        <FormField
          name="execution.periodLoadingTypeId"
          label={
            isInput ? t('periodLoadingTypeId.in') : t('periodLoadingTypeId.out')
          }
          required
        >
          <FormRadioGroup
            name="execution.periodLoadingTypeId"
            onChange={changeLoadingType}
            options={options}
          />
        </FormField>
      )}
      {executionMethod === ExecutionMethod.PERIODIC && (
        <FormField name="execution.periodCount" label="">
          <Flex>
            <PeriodCountField
              name="execution.periodCount"
              disabled={!fixed}
              required={fixed}
              min={1}
            />
            <PeriodCountSuffix>{t('periodCount.suffix')}</PeriodCountSuffix>
          </Flex>
        </FormField>
      )}
      {executionMethod === ExecutionMethod.CALENDAR && (
        <FormField
          name="execution.executionTime"
          label={isInput ? t('executionTime.in') : t('executionTime.out')}
          required
        >
          <FormTimePicker name="execution.executionTime" timeFormat="HH:mm" />
        </FormField>
      )}
      {executionMethod === ExecutionMethod.PERIODIC && (
        <>
          <FormField
            name="execution.periodSkipCount"
            label={t('periodSkipCount.label')}
          >
            <SkipCountField name="execution.periodSkipCount" min={0} />
          </FormField>
          {groupType === DataObjectGroupType.I && (
            <FormField
              name="execution.logLoadingSuccess"
              label={t('logLoadingSuccess.label')}
            >
              <FormCheckbox name="execution.logLoadingSuccess" />
            </FormField>
          )}
        </>
      )}
    </>
  );
}

function usePeriodLoadingOptions(executionMethod?: string) {
  const { t } = useTranslation();
  return useSystemClassifierSelectOptions(['PERIOD_LOADING_TYPE'])
    .PERIOD_LOADING_TYPE.map((option) => ({
      ...option,
      label: t(`option.${option.code}`),
    }))
    .filter((option) =>
      executionMethod === ExecutionMethod.CALENDAR
        ? option.code !== 'FIXED'
        : true
    );
}

const PeriodCountField = styled(FormInputNumber)`
  height: 32px;
  min-width: 64px;
  max-width: 64px;
  margin-right: 8px;
  margin-top: 30px;
`;

const SkipCountField = styled(FormInputNumber)`
  height: 32px;
  min-width: 64px;
  max-width: 64px;
  margin-right: 8px;
`;

const PeriodCountSuffix = styled.span`
  margin-top: 34px;
`;
